/**
 * One vocabulary for "is this row fine or not".
 *
 * The control plane's state machines were each written for their own subsystem
 * and do not share a vocabulary: a node is `ready`, a deployment is `active`, a
 * migration is `applied`, a readiness hint is `verified`, an archive operation
 * is `attention_required`. An operator scanning a screen should not have to
 * know which subsystem they are looking at to know which rows are the problem,
 * so every one of those words maps here to the same five-colour scale.
 */

import type { BadgeColors } from '../components/base/badges/badge-types';

export type Tone = 'success' | 'warning' | 'error' | 'info' | 'neutral';

const TONE_BADGE: Record<Tone, BadgeColors> = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  info: 'blue',
  neutral: 'gray',
};

const SUCCESS = new Set([
  'active',
  'applied',
  'approved',
  'available',
  'committed',
  'completed',
  'connected',
  'delivered',
  'healthy',
  'online',
  'ok',
  'passed',
  'ready',
  'resolved',
  'running',
  'succeeded',
  'success',
  'up',
  'verified',
  // Recovery's own resting state, and the two words the archive-connection
  // ceremony uses for a step that finished cleanly.
  'normal',
  'credential_minted',
  'storage_verified',
]);

const WARNING = new Set([
  // The alert severities themselves. Without these a `severity: warning` alert
  // fell through to the unrecognised-state tone, which is the same blue an
  // informational alert renders in - the two were indistinguishable on the
  // alerts screen.
  'warning',
  'warn',
  'awaiting_approval',
  'canary',
  'candidate',
  'claimed',
  'degraded',
  'draining',
  'hinted',
  'observing',
  'paused',
  'pending',
  'queued',
  'rehearsed',
  'requested',
  'stale',
  'waiting',
  // Durable-job and incident lifecycles, and the recovery ladder's
  // intermediate rungs - none of which are resting states.
  'retry_wait',
  'acknowledged',
  'open',
  'in_progress',
  'submitted',
  'unprocessed',
  'suppressed',
  'vault_encrypted',
  'recovery_frozen',
  'reconciliation_read_only',
  'discrepancy_review',
  'write_enable_pending',
  'controlled_resume',
]);

const ERROR = new Set([
  'aborted',
  'page',
  'attention_required',
  'critical',
  'dead_letter',
  'down',
  'error',
  'failed',
  'fenced',
  'frozen',
  'invalid',
  'quarantined',
  'rejected',
  'timeout',
  'unavailable',
  'unhealthy',
  // A node running an image its role is not supposed to be running is the
  // whole point of the reconciliation screen, so it reads as an error there
  // rather than as an unrecognised state.
  'drift',
  'rolled_back',
  'dead_lettered',
  'escalated',
]);

const NEUTRAL = new Set([
  'archived',
  'disabled',
  'excluded',
  'expired',
  'inactive',
  'not_configured',
  'retired',
  'revoked',
  'cancelled',
  'skipped',
  'superseded',
  'unknown',
]);

export function toneFor(value: string | null | undefined): Tone {
  if (!value) return 'neutral';
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (SUCCESS.has(normalized)) return 'success';
  if (ERROR.has(normalized)) return 'error';
  if (WARNING.has(normalized)) return 'warning';
  if (NEUTRAL.has(normalized)) return 'neutral';
  // An unrecognised state is not automatically fine. Saying "info" rather
  // than "success" keeps a state this console has never seen from rendering
  // in the same green as one that is genuinely healthy.
  return 'info';
}

export function badgeColorFor(value: string | null | undefined): BadgeColors {
  return TONE_BADGE[toneFor(value)];
}

export function badgeColorForTone(tone: Tone): BadgeColors {
  return TONE_BADGE[tone];
}

/** Columns whose value is a lifecycle state worth rendering as a badge. */
const STATUS_KEYS = new Set([
  'state',
  'status',
  'outcome',
  'health',
  'severity',
  'node_state',
  'result',
  'conclusion',
  'event',
]);

export function isStatusKey(key: string): boolean {
  return STATUS_KEYS.has(key);
}

/** HTTP status codes, which are numbers rather than words. */
export function toneForHttpStatus(status: number): Tone {
  if (status >= 500) return 'error';
  if (status >= 400) return 'warning';
  if (status >= 200 && status < 300) return 'success';
  return 'info';
}
